import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";

type ContributionType =
  | "Review"
  | "Discussion"
  | "Editorial"
  | "Feedback"
  | "OSS Library";

interface Contributor {
  name: string;
  role: string;
  contribution: string;
  types: ContributionType[];
}

interface ContributorCardProps extends Contributor {
  isSpecial?: boolean;
}

const ContributorCard: React.FC<ContributorCardProps & { t: (key: string) => string }> = ({
  name,
  role,
  contribution,
  types,
  isSpecial = false,
  t,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`py-2 ${isSpecial ? "text-accent" : ""}`}>
      <div
        className="cursor-pointer group"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-primary font-medium">{name}</div>
            <div className="text-secondary text-sm">{role}</div>
          </div>
          <div className="ml-2 text-secondary group-hover:text-primary transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </div>
        </div>
        <div className="text-xs text-secondary mt-1 group-hover:text-primary/80 transition-colors">
          {isExpanded ? t("acknowledgements.clickToHide") : t("acknowledgements.clickToShow")}
        </div>
      </div>

      {isExpanded && (
        <div className="mt-3 p-3 bg-secondary/30 rounded-lg border border-tertiary">
          <div className="text-sm text-primary mb-2">{contribution}</div>
          <div className="flex flex-wrap gap-1">
            {types.map((type) => (
              <span
                key={type}
                className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-full border border-accent/20"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Acknowledgement: React.FC = () => {
  const { t } = useTranslation("home");
  const acks: Contributor[] = [
    {
      name: "Chi Wang*",
      role: "Senior Staff Research Scientist, Google DeepMind",
      contribution:
        "Original project lead for AutoGen frameworks. This work has benefited immensely from his vision of what mutli-agent frameworks can achieve.",
      types: ["OSS Library"],
    },
    {
      name: "Qingyun Wu",
      role: "Asst. Professor, Penn State",
      contribution:
        "Founding contributor to the AutoGen project and open source community",
      types: ["OSS Library"],
    },
    {
      name: "Saleema Amershi",
      role: "Principal Researcher, Microsoft Research",
      contribution:
        "This work has benefited from multiple discussions related to agents, Human AI collobration and generative AI",
      types: ["Discussion"],
    },
    {
      name: "Adam Fourney",
      role: "Principal Researcher, Microsoft Research",
      contribution:
        "Core contributor to the AutoGen project, lead work on building generalist multi-agent systems (Magentic One) referenced in Chapter 4",
      types: ["Discussion"],
    },
    {
      name: "Gagan Bansal",
      role: "Senior Researcher, Microsoft Research",
      contribution:
        "Core contributor to the AutoGen project, lead work on building generalist multi-agent systems (Magentic One) referenced in Chapter 4",
      types: ["Discussion"],
    },
    {
      name: "Jack Gerrits",
      role: "Principal Research Software Engineer, Microsoft Research",
      contribution:
        "Core contributor to the AutoGen project, lead work on re-architecting AutoGen from v0.4 enabling features such as async agents, message passing and runtimes",
      types: ["OSS Library"],
    },
    {
      name: "Eric Zhu",
      role: "Senior Researcher, Microsoft Research",
      contribution:
        "Core contributor to the AutoGen project, lead work on re-architecting AutoGen from v0.4 enabling features such as async agents, message passing, and AgentChat - a high level api for AutoGen",
      types: ["OSS Library"],
    },
    {
      name: "AutoGen OSS Contributors",
      role: "Open Source Contributors",
      contribution:
        "Provided valuable feedback and insights through the community",
      types: ["Feedback", "OSS Library"],
    },
  ];

  return (
    <div className="border-t border-secondary mt-20 pt-20">
      <div className="bg-primary">
        <div className="mx-auto max-w-7xl pb-12">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-5">
              <h2 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl">
                {t("acknowledgements.title")}
              </h2>
              <p className="mt-4 text-base leading-7 text-secondary">
                {t("acknowledgements.description")}{" "}
                <a
                  href="https://github.com/microsoft/autogen"
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:text-accent/80"
                >
                  {t("acknowledgements.communityLink")}
                </a>
                .
              </p>
            </div>
            <div className="mt-10 lg:col-span-7 lg:mt-0">
              <div className="text-secondary mb-4">{t("acknowledgements.manyThanksTo")}</div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                {acks.map((ack) => (
                  <ContributorCard
                    key={ack.name}
                    {...ack}
                    isSpecial={ack.name === "AutoGen OSS Contributors"}
                    t={t}
                  />
                ))}
              </div>
              <div className="mt-4 text-xs text-secondary">
                {t("acknowledgements.previousAuthorNote")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Acknowledgement;
